import { useState, useCallback, useRef, useEffect } from 'react';

interface UseListViewSelectionProps {
  items: { id: string }[];
  isLoading: boolean;
}

const useListViewSelection = ({
  items,
  isLoading
}: UseListViewSelectionProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(
    items.length > 0 ? items[0].id : null
  );

  const prevLoadingRef = useRef(isLoading);

  useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);
      return;
    }

    // Select first item when loading finishes or if no item is currently selected
    const loadingJustFinished = prevLoadingRef.current && !isLoading;
    if (loadingJustFinished || !selectedId) {
      setSelectedId(items[0].id);
    }

    prevLoadingRef.current = isLoading;
  }, [items, selectedId, isLoading]);

  const handleSelect = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  const handleItemDeleted = useCallback(
    (deletedId: string, onDeletedCallback?: (id: string) => void) => {
      const deletedIndex = items.findIndex(item => item.id === deletedId);
      let nextSelectedId: string | null = null;

      if (deletedIndex !== -1 && items.length > 1) {
        if (deletedIndex < items.length - 1) {
          nextSelectedId = items[deletedIndex + 1].id;
        } else if (deletedIndex > 0) {
          nextSelectedId = items[deletedIndex - 1].id;
        }
      }

      setSelectedId(nextSelectedId);
      onDeletedCallback?.(deletedId);
    },
    [items]
  );

  return {
    selectedId,
    setSelectedId,
    handleSelect,
    handleItemDeleted
  };
};

export default useListViewSelection;
